import { ContextItem, PromptAttachment, PromptSelection } from './kng-model.assistant';

function decodeXmlEntities(value: string): string {
  return value
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');
}

function humanizeFilename(raw: string): string {
  const base = raw.replace(/\.[a-z0-9]{1,5}$/i, '');
  const words = base.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!words) return raw;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function kindFromFilename(name: string): { kind: ContextItem['kind']; icon: string } {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'pdf') return { kind: 'pdf', icon: 'file-earmark-pdf' };
  if (['mp3', 'wav', 'm4a', 'ogg', 'aac', 'flac'].includes(ext)) return { kind: 'audio', icon: 'file-earmark-music' };
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return { kind: 'image', icon: 'file-earmark-image' };
  if (['txt', 'md', 'doc', 'docx', 'odt', 'rtf'].includes(ext)) return { kind: 'text', icon: 'file-earmark-text' };
  return { kind: 'file', icon: 'paperclip' };
}

export function attachmentId(attachment: PromptAttachment): string {
  return (attachment.id || attachment.data || '').trim();
}

export function tableAttachmentLabel(attachment: PromptAttachment): string {
  const tsv = attachment.tsv || '';
  const lines = tsv.replace(/\r\n/g, '\n').split('\n').filter(line => line.trim());
  if (!lines.length) return 'Tableau';
  const cols = (lines[0].match(/\t/g) || []).length + 1;
  const headers = attachment.headers !== false;
  const rows = headers ? Math.max(0, lines.length - 1) : lines.length;
  return `${cols} cols × ${rows} lignes, ${headers ? 'avec' : 'sans'} en-tête`;
}

export function attachmentLabel(attachment: PromptAttachment): string {
  if (attachment.type === 'table') return tableAttachmentLabel(attachment);
  if (attachment.type === 'image') {
    return attachment.filename ? `Image · ${humanizeFilename(attachment.filename)}` : 'Image';
  }
  if (attachment.type === 'email') {
    return attachment.mailbox ? `Email · ${attachment.mailbox}` : 'Email joint';
  }
  if (attachment.type === 'sharepoint') {
    return attachment.title?.trim() || 'Page SharePoint';
  }
  return attachment.filename ? humanizeFilename(attachment.filename) : 'Document joint';
}

export function attachmentIcon(attachment: PromptAttachment): string {
  if (attachment.type === 'table') return 'table';
  if (attachment.type === 'image') return 'file-earmark-image';
  if (attachment.type === 'email') return 'envelope';
  if (attachment.type === 'sharepoint') return 'microsoft';
  return kindFromFilename(attachment.filename || '').icon;
}

export function attachmentsToContextItems(
  attachments: PromptAttachment[],
  selection?: PromptSelection | null,
): ContextItem[] {
  const items = attachments.map((attachment): ContextItem => ({
    kind: attachment.type === 'file'
      ? kindFromFilename(attachment.filename || '').kind
      : attachment.type === 'table'
        ? 'table'
        : attachment.type,
    icon: attachmentIcon(attachment),
    title: attachmentLabel(attachment),
    ...(attachment.webUrl ? { href: attachment.webUrl } : {}),
  }));
  if (selection?.text) {
    items.push({
      kind: 'selection',
      icon: 'quote',
      title: selection.source || 'Extrait sélectionné',
    });
  }
  return items;
}

export function contextItemFromJoin(el: Element): ContextItem | null {
  const tag = el.tagName.toLowerCase();

  if (tag === 'selection') {
    return {
      kind: 'selection',
      icon: 'quote',
      title: decodeXmlEntities(el.getAttribute('source') || '') || 'Extrait sélectionné',
    };
  }

  if (tag !== 'join') return null;

  const type = el.getAttribute('type');
  const body = (el.textContent || '').trim();

  if (type === 'email') {
    const mailbox = decodeXmlEntities(el.getAttribute('mailbox') || '');
    return {
      kind: 'email',
      icon: 'envelope',
      title: body || (mailbox ? `Email · ${mailbox}` : 'Email joint'),
    };
  }

  if (type === 'sharepoint') {
    const webUrl = decodeXmlEntities(el.getAttribute('webUrl') || '');
    const title = decodeXmlEntities(el.getAttribute('title') || '');
    return {
      kind: 'sharepoint',
      icon: 'microsoft',
      title: body || title || 'Page SharePoint',
      ...(webUrl ? { href: webUrl } : {}),
    };
  }

  if (type === 'table') {
    return { kind: 'table', icon: 'table', title: body || 'Tableau' };
  }

  if (type === 'image') {
    const name = decodeXmlEntities(el.getAttribute('nom') || el.getAttribute('filename') || '');
    return {
      kind: 'image',
      icon: 'file-earmark-image',
      title: body || (name ? `Image · ${humanizeFilename(name)}` : 'Image'),
    };
  }

  const name = decodeXmlEntities(
    el.getAttribute('nom') || el.getAttribute('filename') || ''
  );
  const { kind, icon } = name ? kindFromFilename(name) : { kind: 'file' as const, icon: 'paperclip' };
  return { kind, icon, title: body || (name ? humanizeFilename(name) : 'Document joint') };
}

export function splitMessageContext(content: string): { body: string; items: ContextItem[] } {
  if (!content) return { body: '', items: [] };
  const match = content.match(/<context\b[^>]*>([\s\S]*?)<\/context>/i);
  if (!match || match.index === undefined) {
    return { body: content, items: [] };
  }
  const body = `${content.slice(0, match.index)}${content.slice(match.index + match[0].length)}`.trim();
  const items = parseContextInner(match[1]);
  return { body, items };
}

/** Bulle history : chips depuis `joins` ou les tags wire ; body = texte nu. */
export function viewDiscussionMessage(
  msg: { content: string; joins?: ContextItem[] },
  editorAssistant?: { body: string },
): { body: string; joins: ContextItem[] } {
  const parsed = splitMessageContext(msg.content);
  return {
    joins: msg.joins?.length ? msg.joins : parsed.items,
    body: editorAssistant ? editorAssistant.body : parsed.body,
  };
}

function parseContextInner(inner: string): ContextItem[] {
  if (typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(`<div>${inner}</div>`, 'text/html');
  return Array.from(doc.querySelectorAll('join, selection'))
    .map(contextItemFromJoin)
    .filter((item): item is ContextItem => item !== null);
}

export function looksLikeTsv(text: string): boolean {
  const lines = text.replace(/\r\n/g, '\n').split('\n').filter(line => line.trim());
  if (lines.length < 2) return false;
  const withTabs = lines.filter(line => line.includes('\t')).length;
  return withTabs >= Math.ceil(lines.length / 2) && lines[0].split('\t').length >= 2;
}

export function tsvLooksHeader(tsv: string): boolean {
  const first = tsv.replace(/\r\n/g, '\n').split('\n').find(line => line.trim());
  if (!first) return false;
  const cells = first.split('\t').map(cell => cell.trim());
  return cells.length > 0 && cells.every(cell => cell !== '' && !/^-?\d/.test(cell));
}

export function sanitizeHtmlTable(html: string): string | null {
  if (typeof DOMParser === 'undefined') return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const source = doc.querySelector('table');
  if (!source) return null;
  const table = document.createElement('table');
  const copySection = (section: Element | null, tag: string) => {
    if (!section) return;
    const dest = document.createElement(tag);
    Array.from(section.children).forEach(row => {
      if (row.tagName.toLowerCase() !== 'tr') return;
      const tr = document.createElement('tr');
      Array.from(row.children).forEach(cell => {
        const name = cell.tagName.toLowerCase();
        if (name !== 'td' && name !== 'th') return;
        const next = document.createElement(name);
        next.textContent = (cell.textContent || '').trim();
        tr.appendChild(next);
      });
      if (tr.childNodes.length) dest.appendChild(tr);
    });
    if (dest.childNodes.length) table.appendChild(dest);
  };
  copySection(source.querySelector('thead'), 'thead');
  copySection(source.querySelector('tbody') || source, 'tbody');
  return table.querySelector('tr') ? table.outerHTML : null;
}

export function htmlTableToMarkdown(tableHtml: string): string {
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(tableHtml, 'text/html');
  const rows = Array.from(doc.querySelectorAll('tr')).map(row =>
    Array.from(row.querySelectorAll('th,td')).map(cell => (cell.textContent || '').trim().replace(/\|/g, '\\|'))
  ).filter(cells => cells.length);
  if (!rows.length) return '';
  const width = Math.max(...rows.map(row => row.length));
  const pad = (row: string[]) => Array.from({ length: width }, (_, i) => row[i] || '');
  const header = pad(rows[0]);
  const lines = [
    `| ${header.join(' | ')} |`,
    `| ${header.map(() => '---').join(' | ')} |`,
    ...rows.slice(1).map(row => `| ${pad(row).join(' | ')} |`),
  ];
  return lines.join('\n');
}

export function serializeEditorToPrompt(element: HTMLElement): string {
  const parts: string[] = [];
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent || '');
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();
    if (tag === 'table') {
      parts.push('\n' + htmlTableToMarkdown(el.outerHTML) + '\n');
      return;
    }
    if (tag === 'br') {
      parts.push('\n');
      return;
    }
    if (tag === 'p' || tag === 'div') {
      Array.from(el.childNodes).forEach(walk);
      parts.push('\n');
      return;
    }
    Array.from(el.childNodes).forEach(walk);
  };
  Array.from(element.childNodes).forEach(walk);
  return parts.join('').replace(/\n{3,}/g, '\n\n').trim();
}
